import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { useMemo, useCallback } from 'react';
import * as z from 'zod';
import type {
  RJSFSchema,
  UiSchema,
  RegistryWidgetsType,
  TemplatesType,
  FieldsType,
} from '@rjsf/utils';
import type { ZodTypeAny } from 'zod';
import type React from 'react';

export const VERSION_KEY = '_version';

export interface UiAdapter {
  widgets: RegistryWidgetsType;
  templates: TemplatesType;
  /** Optional default Form props an adapter wants to set (e.g., classNames) */
  formOverrides?: Partial<React.ComponentProps<typeof Form>>;
  /** Optional custom fields registry (e.g., a JSON field that wraps CodeMirror). */
  fields?: FieldsType;
}

export interface SchemaConfigMeta {
  id: string;
  title?: string;
  description?: string;
  version: string | number;
  /** Either a Zod schema (preferred) or a JSON Schema (RJSFSchema). */
  schema: ZodTypeAny | RJSFSchema;
}

export interface HeadlessSchemaConfigFormProps<T = unknown> {
  meta: SchemaConfigMeta;
  value?: T;
  onChange?(data: T): void;
  disabled?: boolean;
  enableArrayControls?: boolean;
  /** Inject the design-system adapter (Blueprint, shadcn, etc.) */
  adapter: UiAdapter;
}

function isZodSchema(x: unknown): x is ZodTypeAny {
  return !!x && typeof x === 'object' && typeof (x as any).safeParse === 'function';
}

/** Build/normalize JSON Schema (and remove `_version` from the visible schema). */
export function buildJsonSchema(meta: SchemaConfigMeta): RJSFSchema {
  try {
    const raw: RJSFSchema = isZodSchema(meta.schema)
      ? (z.toJSONSchema(meta.schema) as unknown as RJSFSchema)
      : (meta.schema as RJSFSchema);
    if (raw && raw.type === 'object' && raw.properties && (raw.properties as any)[VERSION_KEY]) {
      const { [VERSION_KEY]: _ignored, ...props } = raw.properties as Record<string, unknown>;
      const req = Array.isArray((raw as any).required)
        ? (raw as any).required.filter((k: string) => k !== VERSION_KEY)
        : (raw as any).required;
      return { ...raw, properties: props, required: req } as RJSFSchema;
    }
    return raw;
  } catch (e) {
    console.warn('toJSONSchema failed', e);
    return { type: 'object', properties: {}, additionalProperties: false };
  }
}

/** Base uiSchema builder (pure data, no component references). */
function buildUiSchema(meta: SchemaConfigMeta, enableArrayControls: boolean | undefined): UiSchema {
  const ui: UiSchema = {
    'ui:submitButtonOptions': { norender: true },
  };

  // Example: array options for a known field
  if (enableArrayControls) {
    ui.loaders = {
      'ui:title': 'Data Loaders',
      'ui:description':
        'Configure multiple data sources for this page. Each loader needs a unique ID.',
      'ui:options': {
        orderable: false,
        addable: true,
        removable: true,
      },
      items: {
        id: {
          'ui:placeholder': 'e.g., project, comments, users',
          'ui:help': 'Unique identifier used in usePageData(id)',
        },
        key: {
          'ui:placeholder': 'e.g., /api/projects/:id',
          'ui:help': 'API endpoint path with route parameters',
        },
        writeMethod: {
          'ui:widget': 'select',
          'ui:placeholder': 'Select HTTP method...',
          'ui:help': 'HTTP method for mutations (optional)',
        },
        transform: {
          'ui:placeholder': 'e.g., transformProjectData',
          'ui:help': 'Transform function name (optional)',
        },
      },
    } as UiSchema;
  }

  // Example: per-widget defaults derived from meta
  if (meta.id === 'button') {
    ui.intent = { 'ui:widget': 'segmented' } as any;
    ui.size = { 'ui:widget': 'segmented' } as any;
  }

  return ui;
}

/** Present value to the form (strip `_version`). */
function toFormData<T>(value?: T): T | undefined {
  if (value && typeof value === 'object') {
    const { [VERSION_KEY]: _vIgnored, ...rest } = value as Record<string, unknown>;
    return rest as T;
  }
  return value;
}

/** Receive value from the form (reattach `_version`). */
function fromFormData<T>(meta: SchemaConfigMeta, data: unknown): T {
  if (data && typeof data === 'object') {
    return { ...(data as Record<string, unknown>), [VERSION_KEY]: meta.version } as T;
  }
  return data as T;
}

export function HeadlessSchemaConfigForm<T = unknown>({
  meta,
  value,
  onChange,
  disabled,
  enableArrayControls,
  adapter,
}: HeadlessSchemaConfigFormProps<T>) {
  const schema = useMemo(() => buildJsonSchema(meta), [meta]);
  const uiSchema = useMemo(() => buildUiSchema(meta, enableArrayControls), [meta, enableArrayControls]);
  const formData = useMemo(() => toFormData<T>(value), [value]);
  // Merge adapter-provided uiSchema overrides with base uiSchema instead of replacing it.
  // One-level deep merge for uiSchema to avoid clobbering nested objects unintentionally
  const mergedUiSchema = useMemo(() => {
    const adapterUi = (adapter?.formOverrides as any)?.uiSchema as UiSchema | undefined;
    if (!adapterUi) return uiSchema;
    const out: UiSchema = { ...uiSchema };
    for (const [k, v] of Object.entries(adapterUi)) {
      const baseVal = out[k as keyof UiSchema] as any;
      if (v && typeof v === 'object' && !Array.isArray(v) && baseVal && typeof baseVal === 'object' && !Array.isArray(baseVal)) {
        out[k as keyof UiSchema] = { ...baseVal, ...(v as object) } as any;
      } else {
        out[k as keyof UiSchema] = v as any;
      }
    }
    return out;
  }, [uiSchema, adapter?.formOverrides]);
  // Avoid passing adapter.uiSchema through to Form props to prevent overriding mergedUiSchema
  const { uiSchema: _omitUi, ...restOverrides } = (adapter?.formOverrides ?? {}) as any;

  const handleChange = useCallback(
    ({ formData: next }: { formData?: unknown }) => {
      if (next !== undefined) onChange?.(fromFormData<T>(meta, next));
    },
    [meta, onChange],
  );

  const handleError = useCallback((errs: unknown[]) => {
    if (Array.isArray(errs) && errs.length) console.warn('Form validation errors:', errs);
  }, []);

  return (
    <Form
      schema={schema}
      uiSchema={mergedUiSchema}
      formData={formData}
      validator={validator}
      onChange={handleChange}
      onError={handleError}
      disabled={disabled}
      showErrorList={false}
      liveValidate
      omitExtraData
      liveOmit
      focusOnFirstError
      noHtml5Validate
      widgets={adapter.widgets}
      templates={adapter.templates}
      fields={adapter.fields}
      {...restOverrides}
    />
  );
}

export default HeadlessSchemaConfigForm;
